import { BareActionContext, getStoreBuilder } from 'vuex-typex';
import axios from 'axios';
import { RootState } from '@/store/state';
import { UsersState } from './state';
import appStore from '@/store/app';
import { Api, ApiResult, ResultOf, SingleResultOf } from '@/types';
import { User as ApplicationUser } from '@/models';
import { Guid } from '@/types';

type StoreContext = BareActionContext<UsersState, RootState>;

const thisState: UsersState = {
    total: 0,
    users: undefined,
    user: undefined,
};


const moduleBuilder = getStoreBuilder<RootState>().module('users', thisState);

const thisModule = {
    getters: {
        users(state: UsersState): ApplicationUser[] | undefined {
            return state.users;
        },

        user(state: UsersState): ApplicationUser | undefined {
            return state.user;
        },

        total(state: UsersState): number {
            return state.total;
        },
    },

    actions: {
        async fetchUsersAsync(context: StoreContext, payload: { pageNumber: number, pageSize: number }): Promise<void> {

            appStore.loading({ isLoading: true });

            return await axios.get<ResultOf<ApplicationUser>>(Api.BaseUrl + `users`, {
                    params: {
                        page: payload.pageNumber,
                        size: payload.pageSize,
                    },
                })
                .then((result) => {
                    commitSetUsers({total: result.data.total, users: result.data.payload});
                })
                .catch((reason) => {
                    if (reason && reason.response) {
                        appStore.errors({status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false});
                    }
                    commitSetUsers({total: 0, users: []});
                })
                .finally(() => {
                    appStore.loading({ isLoading: false });
                });
        },

        async fetchUserAsync(context: StoreContext, payload: {id: string, asNewIfNotExists: boolean }): Promise<void> {

            if (!payload || payload.id === Guid.Empty) {
                if (payload.asNewIfNotExists) {
                    commitSetUser(new ApplicationUser());
                } else {
                    commitSetUser(undefined);
                }
                return;
            }

            appStore.loading({ isLoading: true });

            return await axios.get<SingleResultOf<ApplicationUser>>(Api.BaseUrl + `users/${payload.id}`)
                .then((result) => {
                    commitSetUser(result.data.payload);
                })
                .catch((reason) => {
                    if (reason && reason.response) {
                        appStore.errors({status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false});
                    }
                    commitSetUser(undefined);
                })
                .finally(() => {
                    appStore.loading({ isLoading: false });
                });
        },

        async createUserAsync(context: StoreContext, payload: {username: string, email: string, firstName: string, lastName: string,
                                                               password: string, roles: string[], partnerId: string}): Promise<boolean> {

            if (!payload) { return false; }

            appStore.loading({ isLoading: true });

            return await axios.post<SingleResultOf<string>>(Api.BaseUrl + `users`, payload)
                .then((response) => {
                    appStore.success({message: 'Dato aggiornato'});
                    return true;
                })
                .catch((reason) => {
                    if (reason && reason.response) {
                        appStore.errors({status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false});
                    }
                    return false;
                })
                .finally(() => {
                    appStore.loading({ isLoading: false });
                });
        },

        async updateUserAsync(context: StoreContext, payload: {id: string, username: string, email: string, firstName: string, lastName: string,
                                                               roles: string[], partnerId: string}): Promise<boolean> {

            if (!payload) { return false; }

            appStore.loading({ isLoading: true });

            return await axios.put(Api.BaseUrl + `users/${payload.id}`, payload)
                .then((response) => {
                    appStore.success({message: 'Dato aggiornato'});
                    return true;
                })
                .catch((reason) => {
                    if (reason && reason.response) {
                        appStore.errors({status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false});
                    }
                    return false;
                })
                .finally(() => {
                    appStore.loading({ isLoading: false });
                });
        },

        async deleteUserAsync(context: StoreContext, payload: { id: string }): Promise<boolean> {

            if (!payload) { return false; }

            appStore.loading({ isLoading: true });

            return await axios.delete(Api.BaseUrl + `users/${payload.id}`)
                .then((result) => {
                    commitDeleteUser(payload);
                    appStore.success({message: 'L\'utente è stato eliminato con successo.'});
                    return true;
                })
                .catch((reason) => {
                    if (reason && reason.response) {
                        appStore.errors({status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false});
                    }
                    return false;
                })
                .finally(() => {
                    appStore.loading({ isLoading: false });
                });
        },

        async changePasswordAsync(context: StoreContext, payload: {id: string, currentPassword: string, newPassword: string}): Promise<boolean> {

            if (!payload) { return false; }

            if (payload.currentPassword === payload.newPassword) {
                return false;
            }

            appStore.loading({ isLoading: true });

            return await axios.post(Api.BaseUrl + `identity/${payload.id}/changePassword`, payload)
                .then((result) => {
                    appStore.success({message: 'Password cambiata con successo.'});
                    return true;
                })
                .catch((reason) => {
                    if (reason && reason.response) {
                        appStore.errors({status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false});
                    }
                    return Promise.reject(false);
                })
                .finally(() => {
                    appStore.loading({ isLoading: false });
                });
        },

        async requestResetPasswordAsync(context: StoreContext, payload: { username: string }): Promise<boolean> {

            if (!payload) { return false; }

            if (!payload.username) {
                return false;
            }

            appStore.loading({ isLoading: true });

            return await axios.post(Api.BaseUrl + `identity/requestResetPassword`, payload)
                .then((result) => {
                    appStore.success({message: 'Richiesta di reset della password inviata. Controlla casella di posta.'});
                    return true;
                })
                .catch((reason) => {
                    if (reason && reason.response) {
                        appStore.errors({status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: true});
                    }
                    return Promise.reject(false);
                })
                .finally(() => {
                    appStore.loading({ isLoading: false });
                });
        },

        async resetPasswordAsync(context: StoreContext, payload: { id: string, token: string, password: string, confirmPassword: string }): Promise<boolean> {

            if (!payload) { return false; }

            appStore.loading({ isLoading: true });

            return await axios.post(Api.BaseUrl + `identity/resetPassword`, payload)
                .then((result) => {
                    appStore.success({message: 'La password è stata resettata con successo.'});
                    return true;
                })
                .catch((reason) => {
                    if (reason && reason.response) {
                        appStore.errors({status: reason.response.status, result: reason.response.data as ApiResult, ignoreNotFound: false});
                    }
                    return Promise.reject(false);
                })
                .finally(() => {
                    appStore.loading({ isLoading: false });
                });
        },
    },

    mutations: {
        setUsers(state: UsersState, payload: { total: number, users: ApplicationUser[] }) {
            state.total = payload.total;
            state.users = payload.users;
        },

        setUser(state: UsersState, payload: ApplicationUser | undefined) {
            state.user = payload;
        },

        deleteUser(state: UsersState, payload: { id: string }) {
            if (state.users) {
                const index = state.users.findIndex((user) => user.id === payload.id);
                if (index > -1) {
                    state.users.splice(index, 1);
                }
            }
            if (state.total > 0) {
                state.total--;
            }
        },
    },
};

// private getters
const getUsers = moduleBuilder.read(thisModule.getters.users, 'users');
const getUser = moduleBuilder.read(thisModule.getters.user, 'user');
const getUsersTotal = moduleBuilder.read(thisModule.getters.total, 'getUsersTotal');

// private mutations
const commitSetUsers = moduleBuilder.commit(thisModule.mutations.setUsers, 'setUsers');
const commitSetUser = moduleBuilder.commit(thisModule.mutations.setUser, 'setUser');
const commitDeleteUser = moduleBuilder.commit(thisModule.mutations.deleteUser, 'deleteUser');

const users = {

    // getters (wrapped as real getters)
    get users() { return getUsers(); },
    get user() { return getUser(); },
    get total() { return getUsersTotal(); },

    // actions
    fetchUsersAsync: moduleBuilder.dispatch(thisModule.actions.fetchUsersAsync, 'fetchUsersAsync'),
    fetchUserAsync: moduleBuilder.dispatch(thisModule.actions.fetchUserAsync, 'fetchUserAsync'),
    createUserAsync: moduleBuilder.dispatch(thisModule.actions.createUserAsync, 'createUserAsync'),
    updateUserAsync: moduleBuilder.dispatch(thisModule.actions.updateUserAsync, 'updateUserAsync'),
    deleteUserAsync: moduleBuilder.dispatch(thisModule.actions.deleteUserAsync, 'deleteUserAsync'),
    changePasswordAsync: moduleBuilder.dispatch(thisModule.actions.changePasswordAsync, 'changePasswordAsync'),
    requestResetPasswordAsync: moduleBuilder.dispatch(thisModule.actions.requestResetPasswordAsync, 'requestResetPasswordAsync'),
    resetPasswordAsync: moduleBuilder.dispatch(thisModule.actions.resetPasswordAsync, 'resetPasswordAsync'),
};

export default users;
